import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { UseFormGetValues, UseFormSetValue } from "react-hook-form";
import apiUrls from "src/api";
import ContextSelector from "src/common/components/ContextSelector/ContextSelector";
import {
  getExcludedLayers,
  setExcludedLayers,
  MANDATORY_CONTEXT_LAYERS,
  OPTIONAL_CONTEXT_LAYERS,
  OptionalContextLayer,
} from "../../util/slopifyContextPrefs";
import { useAssistantScenarIaRail } from "./useAssistantScenarIaRail";
import OnboardingComponent from "src/common/util/OnBoardingComponent";
import useOnboardingSteps from "src/common/util/OnboardingTour";
import RailHeader from "./RailHeader";
import ScopePill from "./ScopePill";
import ThreadArea from "./ThreadArea";
import StarterChips from "./StarterChips";
import Composer from "./Composer";
import RateLimitLine from "./RateLimitLine";

export interface AssistantScenarIaRailProps {
  contentId: number | undefined;
  activeLanguageId: number;
  sourceLanguageId: number;
  contentTypeModelName: string;
  setValue: UseFormSetValue<any>;
  getValues: UseFormGetValues<any>;
  setHasGeneratedContent: (value: boolean) => void;
  onGeneratingChange: (value: boolean) => void;
  onFieldsApplied: (fieldNames: string[]) => void;
  dirtyFieldNames: string[];
  disabled: boolean;
  otherEnabledLanguageIds: number[];
  staleFieldsByLanguageId: Record<number, Set<string>>;
  allFillableFieldNames: string[];
  onApplyLanguageTranslations: (
    languageId: number,
    translations: Record<string, unknown>,
  ) => Promise<void> | void;
}

const AssistantScenarIaRail = ({
  contentId,
  activeLanguageId,
  sourceLanguageId,
  contentTypeModelName: _contentTypeModelName,
  setValue,
  getValues,
  setHasGeneratedContent,
  onGeneratingChange,
  onFieldsApplied,
  dirtyFieldNames,
  disabled,
  otherEnabledLanguageIds,
  staleFieldsByLanguageId,
  // `allFillableFieldNames` only fed the disabled propagation chips; kept on
  // the props contract so callers don't need a churn-y refactor when the
  // backend endpoint is fixed and the chips come back.
  allFillableFieldNames: _allFillableFieldNames,
  onApplyLanguageTranslations,
}: AssistantScenarIaRailProps) => {
  useTranslation();
  const onboardingSteps = useOnboardingSteps();
  const {
    prompt,
    setPrompt,
    inFlightTurn,
    liveTurn,
    historyTurns,
    failedTurns,
    manuallyDirtied,
    rateLimitRemaining,
    rateLimitResetAt,
    isGenerating,
    submit,
    abort,
    retry,
    acceptTurn,
    rejectTurn,
    refineTurn,
    restoreTurn,
    resetConversation,
    applyLanguage,
    rejectLanguage,
    applyAllLanguages,
  } = useAssistantScenarIaRail({
    contentId,
    activeLanguageId,
    sourceLanguageId,
    apiUrl: apiUrls.slopify,
    propagateApiUrl: apiUrls.slopifyPropagate,
    setValue,
    getValues,
    setHasGeneratedContent,
    onGeneratingChange,
    onFieldsApplied,
    dirtyFieldNames,
    onApplyLanguageTranslations,
  });

  // Note: propagation-driven chips ("Traduire", "Retraduire les champs
  // obsolètes") are disabled while the propagate endpoint is broken, so
  // canPropagate / hasStaleInActiveLanguage are no longer computed here.

  // Esc-to-abort while a generation is in flight.
  useEffect(() => {
    if (!isGenerating) return undefined;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") abort();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [isGenerating, abort]);

  const composerDisabled = disabled || isGenerating || typeof contentId !== "number";
  // Count only turns the user would actually care about losing:
  // - in-flight (currently generating, can be aborted via reset)
  // - live (proposal awaiting accept/reject)
  // - failed (retryable)
  // - accepted (applied to the form, kept in history for restore)
  // Aborted / rejected turns are already discarded conceptually — persisting
  // them and treating them as "thread has content" was what made the
  // "Nouvelle conversation" button feel clickable on a seemingly-empty rail.
  const hasMeaningfulTurn =
    Boolean(inFlightTurn) ||
    Boolean(liveTurn) ||
    failedTurns.some((t) => t.status === "failed") ||
    historyTurns.some((t) => t.status === "accepted");

  return (
    <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-3 relative h-full flex flex-col">
      <div className="shrink-0">
        <RailHeader
          onNewConversation={resetConversation}
          newConversationDisabled={!hasMeaningfulTurn || isGenerating}
        />
        <div className="assistantScenarIa-step-1">
          <ContextSelector
            i18nBase="general.assistantScenarIa"
            mandatoryLayers={MANDATORY_CONTEXT_LAYERS}
            optionalLayers={OPTIONAL_CONTEXT_LAYERS}
            sourceKey={contentId}
            disabled={typeof contentId !== "number"}
            prefs={{
              getExcluded: () =>
                typeof contentId === "number" ? getExcludedLayers(contentId) : [],
              setExcluded: (e) => {
                if (typeof contentId === "number") {
                  setExcludedLayers(contentId, e as OptionalContextLayer[]);
                }
              },
            }}
          />
          <ScopePill />
        </div>

        <div className="border-t border-gray-100 my-2" />
      </div>

      <div className="assistantScenarIa-step-3 flex-1 min-h-0 overflow-y-auto personalize-scroll-visible -mx-1 px-1">
        <ThreadArea
          inFlightTurn={inFlightTurn}
          liveTurn={liveTurn}
          historyTurns={historyTurns}
          failedTurns={failedTurns}
          manuallyDirtied={manuallyDirtied}
          activeLanguageId={activeLanguageId}
          onAbort={abort}
          onRetry={retry}
          onAccept={acceptTurn}
          onReject={rejectTurn}
          onRefine={refineTurn}
          onRestore={restoreTurn}
          onApplyLanguage={applyLanguage}
          onRejectLanguage={rejectLanguage}
          onApplyAllLanguages={applyAllLanguages}
          sourceValues={getValues() as Record<string, unknown>}
          canPropagate={false}
          targetLanguageIds={otherEnabledLanguageIds}
          staleFieldsByLanguageId={staleFieldsByLanguageId}
        />
      </div>

      <div className="shrink-0">
        <div className="border-t border-gray-100 my-2" />

        {/*
          Propagation (POST /api/slopify/propagate) is currently disabled —
          the backend endpoint returns 400 and surfaced "Échec réseau" cards
          on every retry. The "Traduire" and "Retraduire les champs obsolètes"
          chips, plus the post-accept fan-out, all routed through it. Once
          the propagate endpoint is fixed, restore the chips and the
          onPropagateAccepted handler — bulk DeepL flow via translateProject
          (per-content "Retraduire cette interaction" + project-level page)
          stays available in the meantime.
        */}
        <StarterChips
          disabled={composerDisabled}
          canTranslate={false}
          canRetranslateStale={false}
          onPick={(_key, chipPrompt) => {
            submit(chipPrompt);
          }}
        />
        <div className="assistantScenarIa-step-2">
          <Composer
            prompt={prompt}
            setPrompt={setPrompt}
            onSubmit={(p) => submit(p)}
            disabled={composerDisabled}
          />
        </div>
        <RateLimitLine remaining={rateLimitRemaining} resetAt={rateLimitResetAt} />
      </div>
      <OnboardingComponent
        id="assistantScenarIa"
        relevantArticle="assistantScenarIa"
        steps={onboardingSteps.assistantScenarIa}
      />
    </div>
  );
};

export default AssistantScenarIaRail;
